import React, {useContext, useEffect, useRef, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {FormattedMessage} from "react-intl";

// Material UI
import {Grid} from "@mui/material";

// Components
import CustomButton from "components/CustomButton";
import MyTasks from "components/WMS/Mobile/MyTasks";
import PageTitle from "components/WMS/Mobile/PageTitle";
import Popup from "components/WMS/Mobile/Popup";
import SelfTaskAffectation from "./components/SelfTaskAffectation";
import WmsMobileLayout from "../wmsMobileLayout";
import PickingToastManager, {ToastType} from "./components/PickingToastManager";

// API
import {getTasks, patchTaskStarted} from "api/wms/tasks/list";

// Contexts
import {BarCodeScannerContext} from "contexts/barCodeScannerContext";
import {WMSContext} from "../context/WMSContext";

// Utils
import {WMSRoutes} from "utils/WMSRoutes";

// Assets
import Shelf from "assets/img/wms/shelf.svg";
import Trolley from "assets/img/wms/trolley.svg";
import NoRetrievedTaskToast from "./components/NoRetrievedTaskToast";
import {WmsContainerType} from "../../../utils/WMSContainerType";
import ManualTrolleyChoice from "./components/ManualTrolleyChoice";
import BeatLoaderRegular from "../../../components/loaders/BeatLoaderRegular";

function Picking() {
    const {
        currentInventory,
        currentMember,
        setCurrentTask,
        isMobile,
    } = useContext(WMSContext)

    const {scannedString, setEnableScan, resetScannedString} = useContext(BarCodeScannerContext)

    const [openAffectationModal, setOpenAffectationModal] = useState(false)
    const [openTrolleyModal, setOpenTrolleyModal] = useState(false)
    const [tasks, setTasks] = useState()
    const [retrievedTask, setRetrievedTask] = useState({})
    const [selectedTask, setSelectedTask] = useState()
    const [noRetrievedTaskToast, setNoRetrievedTaskToast] = useState(false)
    const [openManualTrolleyChoiceDrawer, setOpenManualTrolleyChoiceDrawer] = useState(false)

    const location = useLocation()
    const history = useHistory();

    const loading = useRef(true);

    const [toast, setToast] = useState({
        open: false,
        type: 0,
        isError: true
    })

    const handleOpenAffectationModal = () => {
        setOpenAffectationModal(true)
    }

    const handleInit = () => {
        getTasks(currentInventory, currentMember.id).then((response) => {
            setTasks(response);
        }).finally(() => {
            loading.current = false
            setEnableScan(true)
        })
    }

    const handleCloseAffectationModal = () => {
        setOpenAffectationModal(false)
        setRetrievedTask({})
        handleInit()
    }

    const handleOpenTrolleyModal = () => {
        handleCloseAffectationModal()
        setEnableScan(true)
        setOpenTrolleyModal(true)
    }

    const handleCloseTrolleyModal = () => {
        setOpenTrolleyModal(false)
        handleCloseAffectationModal()
    }

    const handleNoRetrievedTaskToast = () => {
        setNoRetrievedTaskToast(false)
    }

    const handleCloseManualTrolleyChoiceDrawer = () => {
        setOpenManualTrolleyChoiceDrawer(false)
    }

    const handleTrolleyChoice = (container) => {
        setEnableScan(false)

        return patchTaskStarted(selectedTask, currentMember, container)
            .then(() => {
                resetScannedString()
                setCurrentTask(selectedTask)
                history.push(`${WMSRoutes.PICKING}/${selectedTask}/${container}`)
            })
            .catch((errorBody) => {
                const error = errorBody?.error

                if (error === 'TROLLEY_ALREADY_TAKEN') {
                    setToast({open: true, type: ToastType.TROLLEY_ALREADY_TAKEN, isError: true})
                } else if (error === 'TROLLEY_CONTAIN_SHIPMENTS') {
                    setToast({open: true, type: ToastType.TROLLEY_CONTAIN_SHIPMENTS, isError: true})
                } else {
                    setToast({open: true, type: ToastType.TROLLEY_NOT_FOUND, isError: true})
                }
            })
            .finally(() => {
                setEnableScan(true)
                resetScannedString()
            })
    }

    function handelSelectedTaskOpenTrolley(task) {
        setSelectedTask(task)
        setOpenTrolleyModal(true)
    }

    // on scan
    useEffect(() => {
        if (currentMember && selectedTask && openTrolleyModal && scannedString && scannedString.length > 0) {
            handleTrolleyChoice(scannedString)
        }
    }, [scannedString]);

    useEffect(() => {
        if (Object.keys(retrievedTask).length > 0) {
            setSelectedTask(retrievedTask)
            setOpenAffectationModal(true)
        } else if (null === retrievedTask) {
            setNoRetrievedTaskToast(true)
        }
    }, [retrievedTask]);

    useEffect(() => {
        if (currentInventory) {
            handleInit()
        }
    }, [currentInventory]);

    // When we came from the MyTasks component with a selected task we have a location.state
    useEffect(() => {
        if (location.state?.task) {
            handelSelectedTaskOpenTrolley(location.state.task)
        }

        if (location.state?.taskCreated) {
            setToast({
                open: true,
                type: ToastType.TASK_CREATED_SUCCESSFULLY,
                isError: false
            })
        }
    }, [location.state]);

    function closeToast() {
        setToast({
            open: false,
            type: 0,
            isError: true
        })
    }

    return (
        <WmsMobileLayout className="wms-test-component">
            <WmsMobileLayout.LeftHeader>

            </WmsMobileLayout.LeftHeader>
            <WmsMobileLayout.RightHeader>

            </WmsMobileLayout.RightHeader>

            <WmsMobileLayout.Content overflow={'unset'}>
                <NoRetrievedTaskToast
                    open={noRetrievedTaskToast}
                    handleClose={handleNoRetrievedTaskToast}
                />

                {openManualTrolleyChoiceDrawer && (
                    <ManualTrolleyChoice
                        handleTrolleyChoice={handleTrolleyChoice}
                        handleClose={handleCloseManualTrolleyChoiceDrawer}
                    />
                )}


                <Popup
                    open={openAffectationModal}
                    onClose={handleCloseAffectationModal}
                    title={
                        <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.title"}/>
                    }
                    subtitle={
                        <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.description"}/>
                    }
                    icon={<img src={Shelf} alt={"Shelf"}/>}
                >
                    <Grid container>
                        <Grid container item direction={"column"}>
                            <CustomButton
                                fullWidth
                                style={{marginBottom: "16px"}}
                                onClick={handleOpenTrolleyModal}
                            >
                                <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.button"}/>
                            </CustomButton>
                            <CustomButton
                                onClick={handleCloseAffectationModal}
                                color={"default"}
                                fullWidth
                            >
                                <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.cancel"}/>
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Popup>
                <Popup
                    open={openTrolleyModal}
                    onClose={handleCloseTrolleyModal}
                    title={
                        <FormattedMessage id={"wms.myPickingMissions.scanTrolley"}/>
                    }
                    subtitle={
                        <FormattedMessage id={"wms.myPickingMissions.scanTrolley.description"}/>
                    }
                    icon={<img src={Trolley} alt={"Trolley"}/>}
                >
                    <CustomButton fullWidth loading={loading.current} disabled={loading.current} onClick={() => {
                        setOpenTrolleyModal(false)
                        setOpenManualTrolleyChoiceDrawer(true)
                    }}>
                        <FormattedMessage id={"wms.myPickingMissions.scanTrolleyModal.manualSelectionButton"}/>
                    </CustomButton>
                </Popup>

                <PageTitle
                    title={<FormattedMessage id={"wms.myPickingMissions.title"}/>}
                    subtitle={<FormattedMessage id={"wms.myPickingMission.missionSubtitle"}/>}
                />

                {loading.current && !tasks && (
                    <Grid
                        item
                        sx={{maxWidth: 'fit-content', margin: 'auto', paddingY: '16px'}}
                    >
                        <BeatLoaderRegular loading={loading.current} size={15}/>
                    </Grid>
                )}

                {tasks && Array.isArray(tasks) && tasks.length > 0 && (
                    <MyTasks
                        passedTask={tasks}
                    />
                )}

                <SelfTaskAffectation
                    setRetrievedTask={setRetrievedTask}
                    setOpenNoTaskToast={setNoRetrievedTaskToast}
                />

                <PickingToastManager
                    open={toast.open}
                    type={toast.type}
                    isErrorToast={toast.isError}
                    handleClose={closeToast}
                />
            </WmsMobileLayout.Content>
        </WmsMobileLayout>
    )
}

export default Picking;
